/**
 * Deleting the trainer's own account: the typed word, the store notice, and the one request.
 *
 * The backend revokes access inside the transaction that accepts the request, so the 204 is the
 * last thing this token succeeds at. Hence the shape, which is the mobile app's: `mutationFn` does
 * exactly ONE thing, so `onError` is structurally unreachable for a racing 401 — rendering one as a
 * retryable failure would be the wrong sentence at the moment an account was successfully deleted.
 *
 * The store notice holds its place while the entitlement is in flight: a disclosure landing late,
 * under a pointer already moving towards „Usuń konto na zawsze", is one nobody read.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { forgetBrandPaint } from '~/entities/brand-color';
import { readSession, routeForSessionStatus, signOut } from '~/entities/session';
import { deleteMeAccount } from '~/shared/api/generated/endpoints/my-account/my-account';
import { leaveDocument } from '~/shared/lib/documentExit';
import { preferenceStore } from '~/shared/lib/preferenceStore';

import { entitlementsQueryOptions } from '../api/accountQueries';
import { matchesConfirmationPhrase } from '../model/accountErasure';
import { storeRenewalNotice, type StoreRenewalNotice } from '../model/storeRenewalNotice';

export interface AccountErasureApi {
  /** What the trainer has typed into the confirmation field. */
  readonly typed: string;
  readonly onTypedChange: (value: string) => void;
  /** Whether the destructive button acts. False until the word matches, and while the act runs. */
  readonly armed: boolean;
  readonly busy: boolean;
  readonly onConfirm: () => void;
  readonly error: string | null;
  /** What to say about a subscription that outlives the account. */
  readonly notice: StoreRenewalNotice;
}

/**
 * @param open whether the dialog is on screen. The entitlement is read for the notice only while it
 *   is, and the typed word is dropped when it closes — a half-typed confirmation must not be
 *   waiting the next time the dialog opens.
 */
export function useEraseAccount(open: boolean): AccountErasureApi {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);

  /**
   * Closing the dialog forgets what was typed.
   *
   * Adjusted DURING RENDER — React's documented "adjust state on prop change" — rather than in an
   * effect, so the dialog's first painted frame is already empty. An effect would show the previous
   * attempt's word for a frame, on a field that arms a delete.
   */
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (!open) {
      setTyped('');
      setError(null);
    }
  }

  const entitlements = useQuery({ ...entitlementsQueryOptions(), enabled: open });

  const erase = useMutation({
    mutationFn: () => deleteMeAccount(),
    onSuccess: () => {
      // The erased account's remembered brand colour goes with it: nothing in this browser may
      // pre-paint an account that no longer exists.
      const erased = readSession().user;
      if (erased !== null) forgetBrandPaint(preferenceStore(), erased.id);
      // `signOut` clears the query cache and the assignment is a full document load, so nothing of
      // this account survives in memory.
      // The account is gone whether or not the local sign-out succeeded, so the navigation happens
      // either way — and the rejection is swallowed rather than left to reach the console, where it
      // would be reported as an unhandled error on a path that has already done its work.
      void signOut()
        .catch(() => undefined)
        .finally(() => {
          leaveDocument(routeForSessionStatus('signedOut') ?? '/sign-in');
        });
    },
    onError: () => {
      // Every failure this path can meet has one answer — see `model/accountErasure` for why the
      // refusal decoder the phone carries is not ported.
      setError(t('web:account.erase.failed'));
    },
  });

  const armed = matchesConfirmationPhrase(typed) && !erase.isPending;

  return {
    typed,
    onTypedChange: (value) => {
      setTyped(value);
      setError(null);
    },
    armed,
    busy: erase.isPending,
    onConfirm: () => {
      if (!armed) return;
      erase.mutate();
    },
    error,
    notice: storeRenewalNotice({
      entitlements: entitlements.data,
      isLoading: open && entitlements.isPending,
      isError: entitlements.isError,
    }),
  };
}
